import {
  Button,
  Grid,
  Heading,
  HStack,
  ModalBody,
  ModalFooter,
  Text,
  VStack
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@/constants';


const ExploreModal = ({ onClose }) => {
  const { hasAnyAuthority } = useAuthRole();

  // add entry points to all pages
  // Add allowedRoles property to restrict access to specific entry points
  const entryPoints = {
    nrOnboarding: {
      title: 'NR Onboarding',
      href: '/funnel/nr-onboarding'
    },
    orders: {
      title: 'Check Active Orders',
      href: '/funnel/orders'
    },
    luluDeals: {
      title: 'Check Active Lulu Deals',
      href: '/funnel/orders/lulu'
    },
    campaigns: {
      title: 'Active Campaigns',
      href: '/campaign'
    },
    verifications: {
      title: 'Verifications',
      href: '/kyc'
    },
    fulfillments: {
      title: 'On hold fulfillments',
      href: '/thunes/fulfillments'
    },
    gomsOnFulfillments: {
      title: 'Goms Fulfillments',
      href: '/goms-orders'
    },
    jobs: {
      title: 'Jobs',
      href: '/jobs'
    },
    configSettings: {
      title: 'Config Settings',
      href: '/config',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.SUPPORT_ROLE, ROLES.MANAGER_ROLE, ROLES.CONFIG_MANAGER_ROLE]
    },
    vdaDashboard: {
      title: 'VDA Dashboard',
      href: '/vda-dashboard'
    },
    nonVerifiedOrderReceipts: {
      title: 'Non Verified Order Receipts',
      href: '/non-verified-order-receipts'
    },
    ManualPayouts: {
      title: 'Manual Payouts',
      href: 'manual-payouts/batch'
    },
    rdaDashboard: {
      title: 'RDA Dashboard',
      href: '/rda-dashboard',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.SUPPORT_ROLE, ROLES.MANAGER_ROLE, ROLES.YBL_RDA_MANAGER, ROLES.RDA_DOWNTIME_MANAGER]
    },
    falconDashboard: {
      title: 'Falcon Service Dashboard',
      href: '/falcon-dashboard',
      allowedRoles: [
        ROLES.ADMIN_ROLE,
        ROLES.SUPPORT_ROLE,
        ROLES.FALCON_ADMIN,
        ROLES.FALCON_OPS_MANAGER,
        ROLES.FALCON_VENDOR_MANAGER,
        ROLES.FALCON_CLIENT_MANAGER,
        ROLES.FALCON_DEVELOPER
      ]
    },
    beneficiaryVerificationDashboard: {
      title: 'Beneficiary Verification Dashboard',
      href: '/beneficiary-verification-dashboard'
    },
    bbpsDashboard: {
      title: 'BBPS Dashboard',
      href: '/bbps-dashboard',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.SUPPORT_ROLE, ROLES.MANAGER_ROLE, ROLES.BBPS_MANAGER_ROLE]
    },
    rfiTransactions: {
      title: 'RFI Transactions',
      href: '/rfi-transactions'
    },
    luluRefundsDashboard: {
      title: 'Lulu Refunds Dashboard',
      href: '/lulu-refunds-dashboard',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.SUPPORT_ROLE, ROLES.MANAGER_ROLE, ROLES.LULU_REFUND_MANAGER_ROLE]
    },
    reconDashboard: {
      title: 'Recon Dashboard',
      href: '/recon-dashboard',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.SUPPORT_ROLE, ROLES.MANAGER_ROLE, ROLES.RECON_ADMIN_ROLE]
    },
    settlementTimelineManager: {
      title: 'Settlement Timeline Manager',
      href: '/settlement-timeline-manager'
    },
    settlementServiceDashboard: {
      title: 'Settlement Service Dashboard',
      href: '/settlement-service-dashboard'
    },
    luluBulkSync: {
      title: 'Lulu Bulk Sync',
      href: '/lulu-bulk-sync'
    },
    fraudCaseManager: {
      title: 'Case Investigation Manager',
      href: '/case-investigation-manager',
      allowedRoles: [
        ROLES.ADMIN_ROLE,
        ROLES.SUPPORT_ROLE,
        ROLES.MANAGER_ROLE,
        ROLES.TRANSFER_COMPLIANCE_MGR_READ,
        ROLES.TRANSFER_COMPLIANCE_MGR_WRITE
      ]
    },
    outboundCalling: {
      title: 'Outbound Calling Dashboard',
      href: '/outbound-calling',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.OUTBOUND_CALLING_ADMIN, ROLES.OUTBOUND_CALLING_AGENT]
    },
    libraDashboard: {
      title: 'Libra Dashboard',
      href: '/libra-dashboard',
      allowedRoles: [ROLES.ADMIN_ROLE, ROLES.OPS_ROLE, ROLES.OPS_MANAGER_ROLE, ROLES.BACKOFFICE_MANAGER_ROLE]
    },
    fxServiceDashboard: {
      title: 'FX Service Dashboard',
      href: '/fx-service-dashboard',
      allowedRoles: [ROLES.FX_SERVICE_MANAGER_ROLE, ROLES.FX_SERVICE_READ_ROLE]
    },
    fastTransferManager: {
      title: 'Fast Transfer Manager',
      href: '/fast-transfer-manager',
      allowedRoles: [ROLES.FAST_TRANSFERS_READ, ROLES.FAST_TRANSFERS_WRITE]
    }
  };

  const navigate = useNavigate();

  const handleClick = (url) => {
    navigate(url);
    onClose && onClose();
  };

  const accessibleEntryPoints = Object.keys(entryPoints).filter((key) => {
    const entryPoint = entryPoints[key];
    if (!entryPoint.allowedRoles || entryPoint.allowedRoles.length === 0) {
      return true;
    }
    return hasAnyAuthority(...entryPoint.allowedRoles);
  });

  return (
    <>
      <ModalBody>
        <VStack alignItems={'stretch'} gap={2}>
          <Heading textAlign={'center'} size={'lg'}>
            Explore
          </Heading>
          <Grid gap={4} gridTemplateColumns={'1fr 1fr'} padding={'4'}>
            {accessibleEntryPoints.map((entryPoint) => (
              <Button
                textAlign={'start'}
                key={entryPoint}
                variant={'link'}
                colorScheme="brand"
                onClick={() => handleClick(entryPoints[entryPoint]?.href)}
              >
                <HStack>
                  <Text>{entryPoints[entryPoint]?.title}</Text>
                  <FaArrowRight />
                </HStack>
              </Button>
            ))}
          </Grid>
        </VStack>
      </ModalBody>
      <ModalFooter></ModalFooter>
    </>
  );
};

export default ExploreModal;
